// Libs
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// Components, Layouts, Pages
import { BaseInput, BaseSelect, Loading } from '~/components/common';
// Others
import { IBaseOption } from '~/utils/interface/common';
import { IFormChangeStatusTask, IQueryChangeStatusTask, ITask } from '~/utils/interface/task';
import { EMPTY_STRING, taskStatusOptions } from '~/utils/constants/common';
import { defaultValuesForm, schema } from './helper';
import { TaskStatusEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { changeStatusTask } from '~/thunks/task/taskThunk';
import { taskActions } from '~/thunks/task/taskSlice';
// Styles, images, icons

type Props = {
  task: ITask;
  onClose: () => void;
};

const ChangeStatusTask = (props: Props) => {
  //#region Destructuring Props
  const { task, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/task']);
  const dispatch = useAppDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormChangeStatusTask>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });

  const watchStatus = useWatch({ control, name: 'status' });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState(false);
  const [listStatusTask] = useState<IBaseOption[]>(taskStatusOptions || []);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (task && task?.status) {
      reset({ status: task?.status, notes: task?.notes || EMPTY_STRING });
      return;
    }

    reset({ status: TaskStatusEnum.TO_DO });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseChangeStatus = () => {
    onClose();
    reset(defaultValuesForm);
  };

  const handleSubmitChangeStatus = (data: IFormChangeStatusTask) => {
    const { status } = data;

    if (!task || !task?.id) return;

    const payload: IQueryChangeStatusTask = {
      taskId: task?.id,
      body: { status, ...(data?.status === TaskStatusEnum.DELAY && { notes: data?.notes }) },
    };

    setIsLoading(true);

    dispatch(changeStatusTask(payload))
      .unwrap()
      .then((res) => {
        dispatch(taskActions.setRefreshTaskList(true));
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
        handleCloseChangeStatus();
      });
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='changeStatusTaskComponent'
      open={true}
      onClose={handleCloseChangeStatus}
      fullWidth
      maxWidth={'sm'}
    >
      <Box component={'form'} onSubmit={handleSubmit(handleSubmitChangeStatus)}>
        <DialogTitle>{t('task_change_status_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 size={12}>
              <Controller
                name='status'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseSelect
                    label={t('task_change_status_label_status')}
                    value={value}
                    options={listStatusTask || []}
                    onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                    errorMessage={errors.status?.message}
                    required
                  />
                )}
              />
            </Grid2>

            {watchStatus === TaskStatusEnum.DELAY && (
              <Grid2 size={12}>
                <Controller
                  name='notes'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      required
                      multiline
                      height={'auto'}
                      rows={6}
                      label={t('task_change_status_label_note')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.notes?.message}
                    />
                  )}
                />
              </Grid2>
            )}
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Button variant='outlined' onClick={handleCloseChangeStatus}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={!isDirty}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </DialogActions>
      </Box>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default ChangeStatusTask;
